/**
 * useSeatAssignment.ts
 * 학생 좌석 배정 Hook
 */
import { useState, useCallback } from 'react';
import {
  seatAssignmentRepository,
  Student,
  SeatStatus,
  AssignSeatRequest,
} from '../repositories/SeatAssignmentRepository';
import { useToast } from '../../../contexts/ToastContextOptimized';

export const useSeatAssignment = () => {
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [seatStatus, setSeatStatus] = useState<SeatStatus | null>(null);
  const [assignmentHistory, setAssignmentHistory] = useState<
    Array<{
      seat_code: string;
      floor_name: string;
      assigned_at: string;
      released_at?: string;
    }>
  >([]);

  /**
   * 학생 검색
   */
  const searchStudents = useCallback(
    async (query: string) => {
      if (!query || query.length < 2) {
        setStudents([]);
        return;
      }

      setLoading(true);
      try {
        const results = await seatAssignmentRepository.searchStudents(query);
        setStudents(results);
      } catch (error: any) {
        console.error('Failed to search students:', error);
        showError('학생 검색 실패', error.message);
        setStudents([]);
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  /**
   * 학생 선택
   */
  const selectStudent = useCallback(
    async (student: Student) => {
      setSelectedStudent(student);
      setLoading(true);

      try {
        const [status, history] = await Promise.all([
          seatAssignmentRepository.getStudentSeatStatus(student.id),
          seatAssignmentRepository.getAssignmentHistory(student.id, 5),
        ]);

        setSeatStatus(status);
        setAssignmentHistory(history);
      } catch (error: any) {
        console.error('Failed to get student info:', error);
        showError('학생 정보 조회 실패', error.message);
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  /**
   * 좌석 배정
   */
  const assignSeat = useCallback(
    async (seatId: string, autoReleasePrevious: boolean = true) => {
      if (!selectedStudent) {
        showError('배정 실패', '학생을 먼저 선택해주세요');
        return false;
      }

      setLoading(true);
      try {
        const request: AssignSeatRequest = {
          seat_id: seatId,
          auto_release_previous: autoReleasePrevious,
        };

        const result = await seatAssignmentRepository.assignSeat(
          selectedStudent.id,
          request
        );

        setSeatStatus(result.seat_status);
        showSuccess('배정 완료', result.message);

        const updatedHistory = await seatAssignmentRepository.getAssignmentHistory(
          selectedStudent.id,
          5
        );
        setAssignmentHistory(updatedHistory);

        return true;
      } catch (error: any) {
        console.error('Failed to assign seat:', error);
        showError('좌석 배정 실패', error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [selectedStudent, showSuccess, showError]
  );

  /**
   * 좌석 해제
   */
  const releaseSeat = useCallback(async () => {
    if (!selectedStudent) {
      showError('해제 실패', '학생을 먼저 선택해주세요');
      return false;
    }

    setLoading(true);
    try {
      const result = await seatAssignmentRepository.releaseSeat(selectedStudent.id);
      setSeatStatus(null);
      showSuccess('해제 완료', result.message);

      const updatedHistory = await seatAssignmentRepository.getAssignmentHistory(
        selectedStudent.id,
        5
      );
      setAssignmentHistory(updatedHistory);

      return true;
    } catch (error: any) {
      console.error('Failed to release seat:', error);
      showError('좌석 해제 실패', error.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [selectedStudent, showSuccess, showError]);

  /**
   * 초기화
   */
  const reset = useCallback(() => {
    setStudents([]);
    setSelectedStudent(null);
    setSeatStatus(null);
    setAssignmentHistory([]);
  }, []);

  return {
    loading,
    students,
    selectedStudent,
    seatStatus,
    assignmentHistory,
    searchStudents,
    selectStudent,
    assignSeat,
    releaseSeat,
    reset,
  };
};
